/**
 * 営業案件行動履歴タイムライン表示コンポーネント
 *
 * 行動履歴を時系列で表示し、編集モードでは追加・更新・削除が可能
 */

import React, { useRef, useState } from 'react';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Chat as ChatIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { formatDateJST } from '../../utils/dateUtils';

interface Activity {
  activity_id?: number;
  case_id?: number;
  activity_date: string;
  activity_type: string;
  description?: string;
  created_by?: string;
  created_by_name?: string;
  created_at?: string;
  updated_at?: string;
}

interface ActivityTimelineProps {
  caseId?: number;
  activities: Activity[];
  readOnly?: boolean;
  onAdd?: (activity: Omit<Activity, 'activity_id' | 'case_id' | 'created_by' | 'created_by_name' | 'created_at' | 'updated_at'>) => Promise<void>;
  onUpdate?: (activityId: number, activity: Omit<Activity, 'activity_id' | 'case_id' | 'created_by' | 'created_by_name' | 'created_at' | 'updated_at'>) => Promise<void>;
  onDelete?: (activityId: number) => Promise<void>;
}

const ACTIVITY_TYPES = [
  { value: '商談', label: '商談', icon: <BusinessIcon /> },
  { value: '電話', label: '電話', icon: <PhoneIcon /> },
  { value: 'メール', label: 'メール', icon: <EmailIcon /> },
  { value: '訪問', label: '訪問', icon: <BusinessIcon /> },
  { value: '社内会議', label: '社内会議', icon: <ChatIcon /> },
  { value: 'その他', label: 'その他', icon: <DescriptionIcon /> }
];

const getActivityIcon = (type: string) => {
  const activityType = ACTIVITY_TYPES.find(t => t.value === type);
  return activityType?.icon || <DescriptionIcon />;
};

export const ActivityTimeline: React.FC<ActivityTimelineProps> = ({
  caseId,
  activities,
  readOnly = false,
  onAdd,
  onUpdate,
  onDelete
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [formData, setFormData] = useState<{
    activity_date: string;
    activity_type: string;
    description: string;
  }>({
    activity_date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
    activity_type: '商談',
    description: ''
  });
  const initialFormDataRef = useRef<typeof formData | null>(null);

  const handleOpenAddDialog = () => {
    setEditingActivity(null);
    const initialData = {
      activity_date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
      activity_type: '商談',
      description: ''
    };
    setFormData(initialData);
    initialFormDataRef.current = initialData;
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (activity: Activity) => {
    setEditingActivity(activity);
    const initialData = {
      activity_date: activity.activity_date,
      activity_type: activity.activity_type,
      description: activity.description || ''
    };
    setFormData(initialData);
    initialFormDataRef.current = initialData;
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingActivity(null);
  };

  // 背景クリック・Esc押下時は、入力内容が初期状態から変化していれば破棄確認する
  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      const dirty = JSON.stringify(formData) !== JSON.stringify(initialFormDataRef.current);
      if (dirty && !window.confirm('入力内容が破棄されます。閉じてよろしいですか？')) {
        return;
      }
    }
    handleCloseDialog();
  };

  const handleSave = async () => {
    if (!formData.activity_date || !formData.activity_type) {
      alert('行動日付と行動種別は必須です');
      return;
    }

    try {
      if (editingActivity && editingActivity.activity_id && onUpdate) {
        await onUpdate(editingActivity.activity_id, formData);
      } else if (onAdd) {
        await onAdd(formData);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save activity:', error);
      alert('行動履歴の保存に失敗しました');
    }
  };

  const handleDelete = async (activityId: number) => {
    if (!confirm('この行動履歴を削除してもよろしいですか?')) {
      return;
    }

    try {
      if (onDelete) {
        await onDelete(activityId);
      }
    } catch (error) {
      console.error('Failed to delete activity:', error);
      alert('行動履歴の削除に失敗しました');
    }
  };

  return (
    <Box>
      {!readOnly && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            行動履歴を追加
          </Button>
        </Box>
      )}

      {activities.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" align="center">
              行動履歴がありません
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Timeline position="right">
          {activities.map((activity, index) => (
            <TimelineItem key={activity.activity_id || index}>
              <TimelineOppositeContent color="text.secondary" sx={{ flex: 0.3 }}>
                <Typography variant="body2">
                  {formatDateJST(activity.activity_date)}
                </Typography>
                {activity.created_by_name && (
                  <Typography variant="caption" color="text.secondary">
                    {activity.created_by_name}
                  </Typography>
                )}
              </TimelineOppositeContent>

              <TimelineSeparator>
                <TimelineDot color="primary">
                  {getActivityIcon(activity.activity_type)}
                </TimelineDot>
                {index < activities.length - 1 && <TimelineConnector />}
              </TimelineSeparator>

              <TimelineContent>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                      <Typography variant="h6" component="span">
                        {activity.activity_type}
                      </Typography>
                      {!readOnly && activity.activity_id && (
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditDialog(activity)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(activity.activity_id!)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      )}
                    </Box>
                    {activity.description && (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {activity.description}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      )}

      {/* 行動履歴追加・編集ダイアログ */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingActivity ? '行動履歴編集' : '行動履歴追加'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="行動日付"
              type="date"
              value={formData.activity_date}
              onChange={(e) => setFormData({ ...formData, activity_date: e.target.value })}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="行動種別"
              select
              value={formData.activity_type}
              onChange={(e) => setFormData({ ...formData, activity_type: e.target.value })}
              required
              fullWidth
            >
              {ACTIVITY_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="行動内容"
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              placeholder="商談内容、次回アクション等を記載してください"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActivityTimeline;
